import type { TFunction } from 'i18next';

import { LegalStructure, ShareType } from 'src/types/governance.types';

export const LEGAL_STRUCTURE_LABELS: Record<LegalStructure, string> = {
  [LegalStructure.FULL_OWNERSHIP]: 'Pleine Propriété',
  [LegalStructure.INDIVISION]: 'Indivision',
  [LegalStructure.SCI]: 'SCI',
  [LegalStructure.SARL_FAMILLE]: 'SARL de Famille',
};

export const isCompanyStructure = (legalStructure?: string | null): boolean =>
  legalStructure === LegalStructure.SCI || legalStructure === LegalStructure.SARL_FAMILLE;

export type CcaVocabKey = 'company' | 'indivision' | 'default';

export const getCcaVocabKey = (legalStructure?: string | null): CcaVocabKey => {
  if (isCompanyStructure(legalStructure)) return 'company';
  if (legalStructure === LegalStructure.INDIVISION) return 'indivision';
  return 'default';
};

export interface StructureVocabulary {
  membersTabLabel: string;
  membersTitle: string;
  addMemberLabel: string;
  conventionLabel: string;
}

export const getStructureVocabulary = (
  t: TFunction,
  legalStructure?: LegalStructure
): StructureVocabulary => {
  switch (legalStructure) {
    case LegalStructure.INDIVISION:
      return {
        membersTabLabel: t('admin.vocabulary.indivision.tab', 'Indivisaires'),
        membersTitle: t('admin.vocabulary.indivision.title', 'Indivisaires du bien'),
        addMemberLabel: t('admin.vocabulary.indivision.add', 'Ajouter un indivisaire'),
        conventionLabel: t('admin.vocabulary.indivision.convention', "Convention d'indivision"),
      };
    case LegalStructure.SCI:
      return {
        membersTabLabel: t('admin.vocabulary.sci.tab', 'Associés'),
        membersTitle: t('admin.vocabulary.sci.title', 'Associés de la SCI'),
        addMemberLabel: t('admin.vocabulary.sci.add', 'Ajouter un associé'),
        conventionLabel: t('admin.vocabulary.sci.convention', 'Statuts de la SCI'),
      };
    case LegalStructure.SARL_FAMILLE:
      return {
        membersTabLabel: t('admin.vocabulary.sarl.tab', 'Associés'),
        membersTitle: t('admin.vocabulary.sarl.title', 'Associés de la SARL'),
        addMemberLabel: t('admin.vocabulary.sarl.add', 'Ajouter un associé'),
        conventionLabel: t('admin.vocabulary.sarl.convention', 'Statuts de la SARL'),
      };
    case LegalStructure.FULL_OWNERSHIP:
      return {
        membersTabLabel: t('admin.vocabulary.fullOwnership.tab', 'Propriétaires'),
        membersTitle: t('admin.vocabulary.fullOwnership.title', 'Propriétaires du bien'),
        addMemberLabel: t('admin.vocabulary.fullOwnership.add', 'Ajouter un propriétaire'),
        conventionLabel: t('admin.vocabulary.fullOwnership.convention', 'Règles du bien'),
      };
    default:
      return {
        membersTabLabel: t('admin.tabs.members', 'Membres du bien'),
        membersTitle: t('admin.members.title', 'Membres du bien'),
        addMemberLabel: t('admin.invitations.add', 'Ajouter un membre'),
        conventionLabel: t('admin.vocabulary.default.convention', 'Règles du bien'),
      };
  }
};

export const getSharesDistributedLabel = (t: TFunction, shareType?: ShareType): string => {
  switch (shareType) {
    case ShareType.PARTS_SOCIALES:
      return t('admin.sharesDistributed.partsSociales', 'Parts distribuées');
    case ShareType.TANTIEMES:
      return t('admin.sharesDistributed.tantiemes', 'Tantièmes distribués');
    case ShareType.PERCENTAGE:
    default:
      return t('admin.sharesDistributed.percentage', 'Pourcentage distribué');
  }
};
